<script setup lang="ts">
import ArticleContactFooter from '../../components/ArticleContactFooter.vue'
import BreadcrumbNav from '../../components/BreadcrumbNav.vue'
import ContactSupportSection from '../../components/ContactSupportSection.vue'

definePageMeta({
  layout: 'pusat-bantuan',
})

const route = useRoute()
const router = useRouter()
const categorySlug = computed(() => route.params.category as string)
const tabSlug = computed(() => route.params.tab as string)
const articleSlug = computed(() => route.params.slug as string)

const { getCategoryBySlug, getArticle, getArticlesByCategory, tabs } = usePusatBantuanData()

const category = computed(() => getCategoryBySlug(categorySlug.value))
const article = computed(() => getArticle(categorySlug.value, articleSlug.value))

if (!category.value || !article.value) {
  throw createError({ statusCode: 404, message: 'Artikel tidak ditemukan' })
}

useHead({
  title: `${article.value?.question} - Pusat Bantuan`,
})

const tabLabel = computed(() => tabs.find(t => t.value === tabSlug.value)?.label || 'FAQ')

// All sibling articles in the same category + tab
const siblingArticles = computed(() => getArticlesByCategory(categorySlug.value, tabSlug.value))

const breadcrumbItems = computed(() => [
  { label: category.value?.title || '', to: `/pusat-bantuan/${categorySlug.value}` },
  { label: tabLabel.value, to: `/pusat-bantuan/${categorySlug.value}` },
  { label: article.value?.question || '' },
])

function navigateToArticle(slug: string) {
  router.push(`/pusat-bantuan/${categorySlug.value}/${tabSlug.value}/${slug}`)
}
</script>

<template>
  <div class="container mx-auto px-4 py-8 space-y-6">
    <Card class="p-6 space-y-5">
      <div class="flex items-start justify-between gap-4">
        <BreadcrumbNav
          :items="breadcrumbItems"
        />
        <span class="text-sm shrink-0">Update: {{ article?.updatedAt }}</span>
      </div>

      <!-- Two-column split panel -->
      <div class="flex flex-col lg:flex-row gap-5">
        <!-- Left: FAQ list -->
        <Card class="lg:w-[320px] lg:shrink-0 overflow-y-auto">
          <button
            v-for="sibling in siblingArticles"
            :key="sibling.slug"
            class="flex items-start w-full px-4 py-3.5 text-left transition-colors cursor-pointer rounded-md"
            :class="[
              sibling.slug === articleSlug
                ? 'bg-primary/5 text-primary font-medium'
                : 'hover:bg-neutral-50 text-neutral-600 border-l-2 border-l-transparent',
            ]"
            @click="navigateToArticle(sibling.slug)"
          >
            <span class="text-sm leading-relaxed">{{ sibling.question }}</span>
          </button>
        </Card>

        <!-- Right: Answer content -->
        <Card class="p-5 flex-1">
          <h2 class="text-base font-semibold text-neutral-800">
            {{ article?.question }}
          </h2>
          <div class="mt-4 article-content text-sm text-neutral-600 leading-relaxed">
            <div v-html="article?.answer" />
            <BaseMdrRateTable v-if="article?.answerComponent === 'MdrRateTable'" />
          </div>
          <ArticleContactFooter />
        </Card>
      </div>
    </Card>

    <ContactSupportSection />
  </div>
</template>

<style scoped>
.article-content :deep(p) {
  margin-bottom: 0;
  line-height: 1.7;
}

.article-content :deep(p + p) {
  margin-top: 0.75rem;
}

.article-content :deep(ol) {
  margin-top: 0;
  margin-bottom: 0;
}

.article-content :deep(li) {
  margin-bottom: 0.5rem;
  line-height: 1.7;
}

.article-content :deep(li:last-child) {
  margin-bottom: 0;
}

.article-content :deep(a) {
  color: #2563eb;
  text-decoration: none;
}

.article-content :deep(a:hover) {
  text-decoration: underline;
}

.article-content :deep(strong) {
  font-weight: 600;
  color: #374151;
}
</style>
